import { useState } from 'react'

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo'
import Icon from '@space-core/components/icon'
import Table from '@space-core/components/table'
import Tooltip from '@space-core/components/tooltip'
import { toastEventDispatch } from '@space-events/toast'
import { ToastType, ToastPosition } from '@space-events/toast/enum'
import type { DatePickerEventDetail } from '@space-shell/common/components/datePicker'
import { DatePicker } from '@space-shell/common/components/datePicker'
import { Languages } from '@space-shell/constant/languages'
import { useAuthStore } from '@space-shell/core/store'

import { useExpirationDate } from '@space-shell/modules/communities/hooks/useExpirationDate'

import { useTranslation } from 'react-i18next'

import { UndefinedContainer } from './styles'
import type { ExpirationDateActionProps } from './types'

import CustomSpinner from '../roleAction/customSpinner'

const INVALID_DATE = 'Invalid Date'

const DATE_PARTS_COUNT = 3

const ExpirationDateAction = ({
  isTheOwner,
  canEdit,
  isLoadingCommunityData,
  communityId,
  member
}: ExpirationDateActionProps) => {
  const initialDate = member.expirationDate
    ? new Date(member.expirationDate).toLocaleDateString(Languages.Pt)
    : undefined

  const { locale } = useAuthStore()
  const { t } = useTranslation()
  const [date, setDate] = useState<string | undefined>(initialDate)
  const { expirationDateQuery, invalidateFollowersQueries } = useExpirationDate()

  const handleChangeDate = ({ detail }: CustomEvent<DatePickerEventDetail>) => {
    if (!canEdit || isTheOwner) return

    const date = detail?.dateFormatted

    if (!date || date === INVALID_DATE || date.split('/')?.length < DATE_PARTS_COUNT) {
      return setDate(undefined)
    }

    setDate(date)

    if (!member.hashImportedId) return

    const [day, month, year] = date.split('/')
    const expirationDateFormatted = `${year}-${month}-${day}`

    expirationDateQuery.mutate(
      {
        hashImportedId: member.hashImportedId,
        ucode: member.ucode,
        expirationDate: expirationDateFormatted
      },
      {
        onSuccess: () => {
          invalidateFollowersQueries(communityId)
          toastEventDispatch({
            type: ToastType.SUCCESS,
            message: t(
              'configuration_sidebar.members_and_permissions.list.expiration_date_success'
            ),
            position: ToastPosition.BOTTOM_RIGHT
          })
        },
        onError: () => {
          toastEventDispatch({
            type: ToastType.DANGER,
            message: t('configuration_sidebar.members_and_permissions.list.expiration_date_error'),
            position: ToastPosition.BOTTOM_RIGHT
          })
        }
      }
    )
  }

  if (!member.hashImportedId)
    return (
      <Table.Data className='_p-0 _align-middle _text-center' dataTest='expiration-date-action'>
        <div className='_d-flex _justify-content-center'>
          <UndefinedContainer className='_py-1 _px-3 _m-0'>
            <Tooltip
              content={t(
                'configuration_sidebar.members_and_permissions.list.expiration_date_undefined_tooltip'
              )}
              position='top'>
              <Icon icon={faCircleInfo} className='_mr-1' />
            </Tooltip>
            {t('configuration_sidebar.members_and_permissions.list.expiration_date_undefined')}
          </UndefinedContainer>
        </div>
      </Table.Data>
    )

  return (
    <Table.Data className='_p-0 _align-middle _text-center' dataTest='expiration-date-action'>
      {isLoadingCommunityData || expirationDateQuery.isLoading ? (
        <CustomSpinner />
      ) : (
        <DatePicker
          placeholder={initialDate}
          onChange={handleChangeDate}
          locale={locale}
          value={date}
        />
      )}
    </Table.Data>
  )
}

export default ExpirationDateAction
